/**
 * Paper Position Closure & Realized P&L Helpers.
 *
 * Resolves open position indices for batch square-offs and calculates today's net realized trade P&L.
 */

export interface TradeHistoryRecord {
  entryTimeISO?: string;
  [key: string]: any;
}

/**
 * Returns an array of array indices corresponding to positions matching the open predicate.
 * Accepts an injected predicate (e.g. `NC.paperPositions.posIsOpen`) to maintain
 * a single source of truth for the position netting model.
 */
export function findOpenPositionIndices<T>(
  positions: T[] | null | undefined,
  isOpenPredicate: (p: T) => boolean,
): number[] {
  if (!Array.isArray(positions) || typeof isOpenPredicate !== "function") {
    return [];
  }
  const indices: number[] = [];
  for (let i = 0; i < positions.length; i++) {
    const p = positions[i];
    if (p && isOpenPredicate(p)) {
      indices.push(i);
    }
  }
  return indices;
}

/**
 * Calculates net realized P&L for trades whose entry time matches today's ISO date string (YYYY-MM-DD).
 */
export function computeTodayRealizedPnl(
  history: TradeHistoryRecord[] | null | undefined,
  todayIsoDate: string,
  tradeNetPnlFn: (h: TradeHistoryRecord) => number,
): number {
  if (
    !Array.isArray(history) ||
    !todayIsoDate ||
    typeof tradeNetPnlFn !== "function"
  ) {
    return 0;
  }
  return history.reduce((sum, h) => {
    if (!h) return sum;
    const entryIso = (h.entryTimeISO || "").split("T")[0];
    return entryIso === todayIsoDate ? sum + tradeNetPnlFn(h) : sum;
  }, 0);
}
